/**
 * The completion id, composed and taken apart in exactly one place.
 *
 *     `${taskDate}:${taskId}:${memberId}`      2026-08-24:mon-robot:m_7f3a
 *
 * Deterministic, unlike `../semainier`'s random `uid()`: the phone and the PC
 * ticking the same box must produce the *same row*, so the merge converges on
 * one value instead of accumulating two rows that each claim to describe
 * Monday's robot run. The id is also the upsert conflict target and half of the
 * primary key, so it is embedded in every row ever stored, locally and
 * remotely — see `CLAUDE.md` §1 and §4.
 *
 * The parsing lives here because the draft did it inline, with
 * `taskKey.split(':', 2)`:
 *
 *     '2026-08-24:mon-robot:m_7f3a'.split(':', 2)
 *     // [ '2026-08-24', 'mon-robot' ]   ← the member is gone, quietly
 *
 * That is the shape of failure this module exists to make impossible: never
 * return a partial answer where a wrong one cannot be seen.
 */

export interface CompletionKey {
  /** ISO date, `YYYY-MM-DD`. */
  taskDate: string
  /** A template id: a slug from `src/data/default-plan.ts`, or a minted `custom-…`. */
  taskId: string
  /** `household_members.id`. */
  memberId: string
}

export const COMPLETION_ID_SEPARATOR = ':'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Compose an id, and refuse to compose a broken one.
 *
 * Throwing looks harsh for something called from a click handler, but every
 * component here is a literal from `src/data/default-plan.ts` or an id minted
 * by `newTaskId` or by `ensure_household` — so a colon in one of them is a
 * configuration or coding fault, not user input. Failing at the point of
 * composition costs one crash; failing silently costs a row that no other
 * device can ever match, and the symptom shows up weeks later as a box that
 * will not stay ticked.
 *
 * The one path where that stopped being true is a chore id arriving from
 * Postgres or from `localStorage` now that chores are data. `buildPlan` filters
 * those through `isUsableTaskId` before they can ever reach here, so this
 * function still only sees ids the app itself produced — see the note in
 * `src/lib/task-id.ts`.
 */
export function encodeCompletionId(key: CompletionKey): string {
  const parts = [key.taskDate, key.taskId, key.memberId]

  for (const part of parts) {
    if (part === '') {
      throw new Error(`Completion id: empty component in ${JSON.stringify(key)}`)
    }
    if (part.includes(COMPLETION_ID_SEPARATOR)) {
      throw new Error(`Completion id: "${part}" contains ${COMPLETION_ID_SEPARATOR}`)
    }
  }

  if (!ISO_DATE.test(key.taskDate)) {
    throw new Error(`Completion id: "${key.taskDate}" is not a YYYY-MM-DD date`)
  }

  return parts.join(COMPLETION_ID_SEPARATOR)
}

/**
 * Take an id apart, or say plainly that it cannot be taken apart.
 *
 * Returns `null` rather than throwing, because the inputs here are the
 * opposite of `encodeCompletionId`'s: rows that arrive from the server, or
 * were persisted by an older version of the app. One unreadable row must not
 * stop the month view from rendering the other four hundred.
 */
export function decodeCompletionId(id: string): CompletionKey | null {
  const parts = id.split(COMPLETION_ID_SEPARATOR)
  if (parts.length !== 3) return null

  const [taskDate, taskId, memberId] = parts as [string, string, string]
  if (!ISO_DATE.test(taskDate) || taskId === '' || memberId === '') return null

  return { taskDate, taskId, memberId }
}
